import Vue from 'vue';
import { getCancelToken } from '../../plugins/Api';
import uniq from 'lodash.uniq';
import uniqid from 'uniqid';
import { validateFormField } from '@/utils/validations';

function getInititalState() {
  return {
    fields: {},
    savedFields: {},
    instanceData: {},
    instanceDataIds: {},
    types: {},
    saving: false,
    loadingFieldsByForm: {},
    loadingDataByForm: {},
    loadedDataByForm: {},
    dataSaving: false,
    validating: {},
    validation: {},
    dirty: [],
    finishedLoadingAll: false,
    formsCSVFormatData: null,
    loadingList: false,
    list: [],
    listTotal: 0,
    deletingFormUuid: null,
  };
}

export const Forms = {
  namespaced: true,

  state: getInititalState(),

  getters: {
    getFieldsForForm: (state) => (uuid) => {
      return state.fields[uuid] ?? [];
    },

    isDataSaving: ({ dataSaving }) => dataSaving,

    getInstanceData:
      ({ instanceData }) =>
      (instanceId) =>
        instanceData[instanceId] ?? {},

    getInstanceDataIds:
      ({ instanceDataIds }) =>
      (instanceId) =>
        instanceDataIds[instanceId] ?? [],

    isFormLoaded: (state) => (uuid) => {
      return Object.keys(state.fields).includes(uuid);
    },

    isFormValidating:
      ({ validating }) =>
      (uuid) => {
        return validating[uuid] ?? false;
      },

    getFormValidationStatus:
      ({ validation }) =>
      (uuid) => {
        return validation[uuid] ?? {};
      },

    isFormDirty: (state) => (uuid) => state.dirty.includes(uuid),

    isSomeFormFieldLoading: ({ loadingFieldsByForm }) => Object.values(loadingFieldsByForm).some((v) => v),

    isSomeFormDataLoading: ({ loadingDataByForm }) => Object.values(loadingDataByForm).some((v) => v),

    isSomeFormValidating: ({ validating }) => Object.values(validating).some((v) => v),

    isFormDataLoaded:
      ({ loadedDataByForm }) =>
      (uuid) =>
        !!loadedDataByForm[uuid],

    isFormInstanceEmpty: (_, getters) => (instanceId) => {
      const { isFormDataLoaded, getInstanceData, getInstanceDataIds } = getters;
      return !instanceId || !isFormDataLoaded(instanceId)
        ? null
        : !Object.entries(getInstanceData(instanceId)).length && !getInstanceDataIds(instanceId).length;
    },

    getFinishedLoadingAll(state) {
      return state.finishedLoadingAll;
    },

    getDataToCSVFormatting: ({ formsCSVFormatData }) => formsCSVFormatData,

    getOriginalFields: (state) => (uuid) => state.savedFields[uuid] ?? [],
  },

  mutations: {
    setFieldsLoadingByForm(state, { uuid, value }) {
      Vue.set(state.loadingFieldsByForm, uuid, value);
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    updateFormFields(state, data) {
      const [uuid] = Object.keys(data);
      const formattedData = { ...data };
      formattedData[uuid] = formattedData[uuid].map(validateFormField);
      state.fields = { ...state.fields, ...formattedData };
    },

    updateFormField(state, { uuid, index, fieldData }) {
      let newFields = [...state.fields[uuid]];

      newFields[index] = fieldData.id ? fieldData : { ...fieldData, _temp_id: uniqid() };

      newFields = newFields.map(validateFormField);

      Vue.set(state, 'fields', { ...state.fields, [uuid]: newFields });
    },

    setSavedFormFields(state, data) {
      state.savedFields = { ...state.savedFields, ...data };
    },

    updateFormTypes(state, types) {
      state.types = { ...types };
    },

    setFormDirty(state, uuid) {
      state.dirty = uniq([...state.dirty, uuid]);
    },

    setFormClean(state, uuid) {
      state.dirty = state.dirty.filter((item) => item !== uuid);
    },

    setValidating(state, { form, validating }) {
      state.validating = { ...state.validating, [form]: validating };
    },

    setValidationResult(state, { form, validation }) {
      state.validation = { ...state.validation, [form]: validation };
    },

    resetValidationResult(state, form) {
      Vue.set(state.validation, form, {});
    },

    setInstanceData(state, { instanceId, data }) {
      Vue.set(state.instanceData, instanceId, data);
    },

    setInstanceDataIds(state, { instanceId, dataIds }) {
      state.instanceDataIds = { ...state.instanceDataIds, [instanceId]: dataIds };
    },

    setDataLoadingByForm(state, { instanceId, value }) {
      Vue.set(state.loadingDataByForm, instanceId, value);
    },

    setDataLoadedByForm(state, { instanceId, value }) {
      Vue.set(state.loadedDataByForm, instanceId, value);
    },

    setDataSaving(state, saving) {
      state.dataSaving = saving;
    },

    resetState(state) {
      Object.assign(state, getInititalState());
    },

    setFinishedLoadingAll(state, loading) {
      state.finishedLoadingAll = loading;
    },

    setFormFieldsCSVExportData(state, dataToCSVFormat) {
      state.formsCSVFormatData = dataToCSVFormat;
    },

    setLoadingList(state, loading) {
      state.loadingList = loading;
    },

    updateList(state, list) {
      state.list = list;
    },

    setDeletingFormUuid(state, uuid) {
      state.deletingFormUuid = uuid;
    },

    setListTotal(state, total) {
      state.listTotal = total;
    }
  },

  actions: {
    async loadFormFields({ commit }, uuid) {
      commit('setFieldsLoadingByForm', { uuid, value: true });
      let formFields = [];
      try {
        const {
          data: { data, types },
        } = await this._vm.$api('forms.fields.index', { version: '1.0', form: uuid });
        commit('setSavedFormFields', { [uuid]: data });
        commit('updateFormTypes', types);

        formFields = data;

        if (data) {
          const csvExportData = data.map((item) => {
            const { position, title, type, var_name, options } = item;
            return { position, title, type, var_name, options };
          });

          commit('setFormFieldsCSVExportData', csvExportData);
        }
      } catch (err) {
        console.error(err);
      }

      commit('updateFormFields', { [uuid]: formFields });
      commit('setFieldsLoadingByForm', { uuid, value: false });
    },

    updateFormFields({ commit }, { uuid, fields }) {
      commit('updateFormFields', { [uuid]: [...fields] });
      commit('setFormDirty', uuid);
    },

    updateFormField({ commit }, { uuid, index, fieldData }) {
      commit('updateFormField', { uuid, index, fieldData });
      commit('setFormDirty', uuid);
    },

    revertFieldChanges({ commit, state }, uuid) {
      commit('updateFormFields', { [uuid]: [...state.savedFields[uuid]] });
      commit('setFormClean', uuid);
    },

    async saveFieldChanges({ commit }, { uuid, data, sequentialSaving = false }) {
      commit('setSaving', true);

      let response;
      try {
        response = await this._vm.$api('forms.fields.update', { version: '1.0', form: uuid }, { fields: data });
        if (sequentialSaving) return true;
        commit('updateFormFields', { [uuid]: response.data.data });
        commit('setSavedFormFields', { [uuid]: response.data.data });
        commit('setFormClean', uuid);
        commit('setSaving', false);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setSaving', false);
      }
      return response;
    },

    async validateForm({ commit }, { form, data, reset = true, formInstanceId, canCancelToken = true }) {
      const key = formInstanceId || form;
      let cancelToken;
      let newCancelToken;

      commit('setValidating', { form: key, validating: true });

      if (canCancelToken) {
        cancelToken = getCancelToken(key);
        cancelToken.cancel();
        newCancelToken = getCancelToken(key, true);
      }

      let validation = {};

      if (reset) commit('resetValidationResult', key);

      try {
        const res = await this._vm.$api('forms.validate', { version: '1.0', form }, data, newCancelToken?.token);
        validation = res.data.data;
      } catch (err) {
        commit('resetValidationResult', key);
        console.error(err);
      }
      commit('setValidationResult', { form: key, validation });

      commit('setValidating', { form: key, validating: false });
    },

    async loadInstanceData({ commit }, instanceId) {
      commit('setDataLoadingByForm', { instanceId, value: true });
      commit('setDataLoadedByForm', { instanceId, value: false });
      try {
        let instanceData = await this._vm.$api('forms.instances.data', {
          version: '1.0',
          instance: instanceId,
        });

        commit('setInstanceData', { instanceId, data: instanceData?.data.data });
        commit('setInstanceDataIds', { instanceId, dataIds: instanceData?.data.dataIds });
      } catch (err) {
        console.error(err);
      }
      commit('setDataLoadingByForm', { instanceId, value: false });
      commit('setDataLoadedByForm', { instanceId, value: true });
    },

    async saveInstanceData({ commit }, { instanceId, data }) {
      if (!instanceId) return;
      commit('setDataSaving', true);
      const cancelToken = getCancelToken(instanceId);
      cancelToken.cancel();
      const newCancelToken = getCancelToken(instanceId, true);

      try {
        let instanceData = await this._vm.$api(
          'forms.instances.data.save',
          {
            version: '1.0',
            instance: instanceId,
          },
          data,
          newCancelToken.token
        );

        commit('setInstanceData', { instanceId, data: instanceData?.data.data });
        commit('setInstanceDataIds', { instanceId, dataIds: instanceData?.data.dataIds });
      } catch (err) {
        console.error(err);
      }
      commit('setDataSaving', false);
    },

    setValidating({ commit }, { formUuid, value }) {
      commit('setValidating', { form: formUuid, validating: value });
    },

    resetFormState({ commit }) {
      commit('resetState');
    },

    updateFinishedLoadingAll({ commit }, value) {
      commit('setFinishedLoadingAll', value);
    },

    listForms({ commit }, { filters, page, limit }) {
      commit('setLoadingList', true);

      if (filters) {
        filters = filters.filter(function (fil) {
          if (fil.value.length !== 0) return fil;
        });
      }

      this._vm
        .$api('users.current.forms.index', {
          version: '1.0',
          filters,
          limit,
          page,
        })
        .then(({ data }) => {
          commit('updateList', data.data);
          commit('setListTotal', data.meta.total);
        })
        .finally(() => {
          commit('setLoadingList', false);
        });
    },

    deleteForm({ commit }, { form, comment }) {
      commit('setDeletingFormUuid', form);

      return this._vm
        .$api(
          'forms.destroy',
          {
            version: '1.0',
            form,
          },
          { comment }
        )
        .finally(() => {
          commit('setDeletingFormUuid', null);
        });
    },
  },
};
